import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Upload, Trash2, FileText, Download, Eye, Search, Filter, Brain, Calendar as CalendarIcon, CheckSquare, X, Info } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  
  // OCR & AI Features
  const [showDocDetailModal, setShowDocDetailModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState({});
  const [summaryProcessing, setSummaryProcessing] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // File input ref for resetting
  const fileInputRef = React.useRef(null);
  
  const [categoryForm, setCategoryForm] = useState({
    category_name: '',
    category_label: ''
  });
  const [uploadForm, setUploadForm] = useState({
    client_id: '',
    category: '',
    financial_year: '',
    tags: '',
    description: '',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
    fetchClients();
    fetchCategories();
    fetchFinancialYears();
  }, []);

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') params.append('client_id', selectedClient);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedFinancialYear && selectedFinancialYear !== 'all') params.append('financial_year', selectedFinancialYear);
      
      const response = await axios.get(`${API}/documents?${params}`);
      const docs = response.data;
      setDocuments(docs);
      
      // Calculate stats from documents
      const totalStorage = docs.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
      const categoriesSet = new Set(docs.map(doc => doc.category));
      const fySet = new Set(docs.map(doc => doc.financial_year));
      
      setStats({
        total_documents: docs.length,
        total_storage_mb: (totalStorage / (1024 * 1024)).toFixed(2),
        categories: Object.fromEntries([...categoriesSet].map(cat => [cat, docs.filter(d => d.category === cat).length])),
        financial_years: Object.fromEntries([...fySet].map(fy => [fy, docs.filter(d => d.financial_year === fy).length]))
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/documents/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // OCR & AI Functions
  const handleOCR = async (docId) => {
    try {
      setOcrProcessing(prev => ({ ...prev, [docId]: true }));
      
      const response = await axios.post(`${API}/documents/${docId}/ocr`);
      
      alert('OCR processing completed! Text has been extracted from the document.');
      
      // Refresh documents to show updated data
      fetchDocuments();
      
      // If viewing detail, refresh it
      if (selectedDoc && selectedDoc.id === docId) {
        const updatedDoc = await axios.get(`${API}/documents?client_id=${selectedDoc.client_id}`);
        const doc = updatedDoc.data.find(d => d.id === docId);
        setSelectedDoc(doc);
      }
    } catch (error) {
      console.error('OCR Error:', error);
      alert(error.response?.data?.detail || 'Failed to process OCR');
    } finally {
      setOcrProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleSummarize = async (docId) => {
    try {
      setSummaryProcessing(prev => ({ ...prev, [docId]: true }));
      
      const response = await axios.post(`${API}/documents/${docId}/summarize`);
      
      alert('Summary generated successfully!');
      
      // Refresh documents
      fetchDocuments();
      
      // If viewing detail, refresh it
      if (selectedDoc && selectedDoc.id === docId) {
        const updatedDoc = await axios.get(`${API}/documents?client_id=${selectedDoc.client_id}`);
        const doc = updatedDoc.data.find(d => d.id === docId);
        setSelectedDoc(doc);
      }
    } catch (error) {
      console.error('Summarization Error:', error);
      alert(error.response?.data?.detail || 'Failed to generate summary. Make sure you have added your OpenAI API key in settings.');
    } finally {
      setSummaryProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleBulkOCR = async () => {
    if (selectedDocs.length === 0) {
      alert('Please select documents to process');
      return;
    }

    if (!confirm(`Process OCR on ${selectedDocs.length} document(s)?`)) {
      return;
    }

    try {
      setBulkProcessing(true);
      
      const response = await axios.post(`${API}/documents/bulk-ocr`, selectedDocs);
      
      alert(`Bulk OCR completed!\nSuccessful: ${response.data.successful.length}\nFailed: ${response.data.failed.length}`);
      
      // Clear selection and refresh
      setSelectedDocs([]);
      fetchDocuments();
    } catch (error) {
      console.error('Bulk OCR Error:', error);
      alert('Failed to process bulk OCR');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchDocuments();
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('q', searchQuery);
      if (selectedClient && selectedClient !== 'all') params.append('client_id', selectedClient);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);

      const response = await axios.get(`${API}/documents/search?${params}`);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Search Error:', error);
      alert('Search failed');
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const selectAllDocs = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d.id));
    }
  };

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API}/documents/financial-years`);
      setFinancialYears(response.data.financial_years);
    } catch (error) {
      console.error('Error fetching financial years:', error);
    }
  };

  // Removed fetchStats function - not needed for documents component

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.client_id || !uploadForm.category) {
      alert('Please fill all required fields');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('client_id', uploadForm.client_id);
      formData.append('category', uploadForm.category);
      if (uploadForm.financial_year) formData.append('financial_year', uploadForm.financial_year);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);
      if (uploadForm.description) formData.append('description', uploadForm.description);

      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      
      console.log('Uploading document...');
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 60000 // 60 second timeout for large files
      });

      console.log('Document uploaded successfully:', response.data);

      // Reset form and file input
      setUploadForm({
        client_id: '',
        category: '',
        financial_year: '',
        tags: '',
        description: '',
        file: null
      });
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Close modal
      setShowUploadModal(false);
      
      // Refresh documents list
      await fetchDocuments();
      
      // Show success message
      alert(`✅ Document uploaded successfully!\n\nFile: ${uploadForm.file.name}\nClient: ${clients.find(c => c.id === uploadForm.client_id)?.name || 'Unknown'}\nCategory: ${categories.find(c => c.value === uploadForm.category)?.label || uploadForm.category}`);
      
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`❌ Error uploading document:\n\n${errorMsg}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (window.confirm(`Are you sure you want to delete ${filename}?`)) {
      try {
        await axios.delete(`${API}/documents/${documentId}`);
        fetchDocuments();
        alert('Document deleted successfully!');
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Error deleting document: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 30 * 1024 * 1024) { // 30MB limit
        alert('File size must be less than 30MB');
        return;
      }
      setUploadForm({...uploadForm, file});
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryColor = (category) => {
    const colors = {
      invoices: 'bg-blue-100 text-blue-800',
      receipts: 'bg-green-100 text-green-800',
      tax_returns: 'bg-purple-100 text-purple-800',
      agreements: 'bg-yellow-100 text-yellow-800',
      bank_statements: 'bg-indigo-100 text-indigo-800',
      expense_receipts: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-orange-100 text-orange-800'; // Default for custom categories
  };

  // Category Management Functions
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.category_name.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      await axios.post(`${API}/documents/categories`, {
        category_name: categoryForm.category_name.trim(),
        category_label: categoryForm.category_label.trim() || categoryForm.category_name.trim()
      });

      setShowCategoryModal(false);
      setCategoryForm({ category_name: '', category_label: '' });
      fetchCategories(); // Refresh categories
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Apply filters when they change
  useEffect(() => {
    fetchDocuments();
  }, [selectedClient, selectedCategory, selectedFinancialYear]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Header Card */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <CardTitle className="text-3xl font-bold flex items-center">
              <Upload className="w-8 h-8 mr-3" />
              Document Management
              <Badge className="ml-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 px-3 py-1 text-sm font-bold animate-pulse">
                ✨ AI-Powered
              </Badge>
            </CardTitle>
            <CardDescription className="text-blue-100 text-lg">
              Secure cloud storage with OCR & AI intelligence for all your client documents
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Documents</p>
                    <p className="text-3xl font-bold mt-2">{stats.total_documents || 0}</p>
                  </div>
                  <FileText className="w-12 h-12 text-blue-200" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Storage Used</p>
                    <p className="text-3xl font-bold mt-2">{stats.total_storage_mb || 0} MB</p>
                  </div>
                  <Upload className="w-12 h-12 text-green-200" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Categories</p>
                    <p className="text-3xl font-bold mt-2">{Object.keys(stats.categories || {}).length}</p>
                  </div>
                  <FileText className="w-12 h-12 text-purple-200" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Financial Years</p>
                    <p className="text-3xl font-bold mt-2">{Object.keys(stats.financial_years || {}).length}</p>
                  </div>
                  <CalendarIcon className="w-12 h-12 text-orange-200" />
                </div>
              </div>
            </div>

            {/* Actions and Filters */}

            {/* Search Bar & Bulk Actions */}
            <div className="mb-6 space-y-4">
              {/* Search Bar */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search documents by content, filename, description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                {searchQuery && (
                  <Button onClick={() => { setSearchQuery(''); fetchDocuments(); }} variant="outline">
                    Clear
                  </Button>
                )}
              </div>

              {/* Bulk Operations */}
              {selectedDocs.length > 0 && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">
                          {selectedDocs.length} document(s) selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleBulkOCR}
                          disabled={bulkProcessing}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {bulkProcessing ? (
                            <>Processing...</>
                          ) : (
                            <>
                              <FileText className="w-4 h-4 mr-2" />
                              Bulk OCR
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedDocs([])}
                        >
                          Clear Selection
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} ({client.organization_name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedFinancialYear} onValueChange={setSelectedFinancialYear}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by FY" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Financial Years</SelectItem>
                    {financialYears.map((fy) => (
                      <SelectItem key={fy.value} value={fy.value}>
                        {fy.label} {fy.is_current && '(Current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowCategoryModal(true)}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
                <Button 
                  onClick={() => setShowUploadModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </div>
            </div>

            {/* Documents Table */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Documents Found</h3>
                <p className="text-gray-500 mb-6">Upload your first document to get started</p>
                <Button 
                  onClick={() => setShowUploadModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedDocs.length === documents.length && documents.length > 0}
                          onChange={selectAllDocs}
                          className="w-4 h-4"
                        />
                      </TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Financial Year</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedDocs.includes(doc.id)}
                            onChange={() => toggleDocSelection(doc.id)}
                            className="w-4 h-4"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-blue-600 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">{doc.original_filename}</p>
                              {doc.description && (
                                <p className="text-sm text-gray-500">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{doc.client_name}</p>
                            <p className="text-sm text-gray-500">{doc.client_organization}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getCategoryColor(doc.category)}>
                            {categories.find(c => c.value === doc.category)?.label || doc.category}
                          </Badge>
                        </TableCell>
                        <TableCell>{doc.financial_year}</TableCell>
                        <TableCell>
                          <Badge variant={doc.ocr_processed ? 'default' : 'outline'}>
                            {doc.ocr_processed ? '✅ Processed' : '⏳ Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                        <TableCell>{new Date(doc.uploaded_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {/* View/Detail Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedDoc(doc);
                                setShowDocDetailModal(true);
                              }}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {/* OCR Button */}
                            {!doc.ocr_processed && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOCR(doc.id)}
                                disabled={ocrProcessing[doc.id]}
                                title="Extract Text"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                {ocrProcessing[doc.id] ? (
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <FileText className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            {/* Summarize Button */}
                            {doc.ocr_processed && !doc.summary && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSummarize(doc.id)}
                                disabled={summaryProcessing[doc.id]}
                                title="Generate AI Summary"
                                className="text-purple-600 hover:text-purple-700"
                              >
                                {summaryProcessing[doc.id] ? (
                                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Brain className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            {/* Download Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(doc.s3_url, '_blank')}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            
                            {/* Delete Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(doc.id, doc.original_filename)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>



        {/* Document Detail Modal */}
        {showDocDetailModal && selectedDoc && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-2xl">{selectedDoc.original_filename}</CardTitle>
                    <CardDescription className="text-blue-100 mt-2">
                      Document Details & Intelligence
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDocDetailModal(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Document Info */}
                <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <Badge className="mt-1">{selectedDoc.category}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Financial Year</p>
                    <p className="font-medium mt-1">{selectedDoc.financial_year}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">File Size</p>
                    <p className="font-medium mt-1">{formatFileSize(selectedDoc.file_size)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Uploaded</p>
                    <p className="font-medium mt-1">{new Date(selectedDoc.uploaded_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* OCR Status & Actions */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Document Intelligence</h3>
                      <p className="text-sm text-gray-600">
                        {selectedDoc.ocr_processed
                          ? '✅ Text extracted'
                          : '⏳ OCR not processed yet'}
                        {selectedDoc.summary && ' • ✅ Summary generated'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!selectedDoc.ocr_processed && (
                        <Button
                          onClick={() => handleOCR(selectedDoc.id)}
                          disabled={ocrProcessing[selectedDoc.id]}
                          size="sm"
                        >
                          {ocrProcessing[selectedDoc.id] ? 'Processing...' : 'Extract Text'}
                        </Button>
                      )}
                      {selectedDoc.ocr_processed && !selectedDoc.summary && (
                        <Button
                          onClick={() => handleSummarize(selectedDoc.id)}
                          disabled={summaryProcessing[selectedDoc.id]}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {summaryProcessing[selectedDoc.id] ? 'Generating...' : 'Generate Summary'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary Section */}
                {selectedDoc.summary && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center">
                      <Brain className="w-5 h-5 mr-2 text-purple-600" />
                      AI-Generated Summary
                    </h3>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedDoc.summary}</p>
                    </div>
                  </div>
                )}

                {/* Extracted Data */}
                {selectedDoc.extracted_data && Object.keys(selectedDoc.extracted_data).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Extracted Information</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {selectedDoc.extracted_data.dates && selectedDoc.extracted_data.dates.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">📅 Dates</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedDoc.extracted_data.dates.map((date, idx) => (
                              <Badge key={idx} variant="outline">{date}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedDoc.extracted_data.amounts && selectedDoc.extracted_data.amounts.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">💰 Amounts</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedDoc.extracted_data.amounts.map((amount, idx) => (
                              <Badge key={idx} variant="outline" className="bg-green-50">{amount}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedDoc.extracted_data.parties && selectedDoc.extracted_data.parties.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">👥 Parties Involved</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedDoc.extracted_data.parties.map((party, idx) => (
                              <Badge key={idx} variant="outline">{party}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedDoc.extracted_data.numbers && selectedDoc.extracted_data.numbers.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">🔢 Reference Numbers</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedDoc.extracted_data.numbers.map((number, idx) => (
                              <Badge key={idx} variant="outline">{number}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* OCR Text */}
                {selectedDoc.ocr_text && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                      Extracted Text
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                        {selectedDoc.ocr_text}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => window.open(selectedDoc.s3_url, '_blank')}
                    variant="outline"
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => setShowDocDetailModal(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-blue-600" />
                  Upload Document
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Upload a new document to secure cloud storage (Max 30MB)
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <Label htmlFor="file">Document File *</Label>
                  <Input
                    ref={fileInputRef}
                    id="file"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Supported: PDF, Images, Office docs, Text files, Archives (Max 30MB)
                  </p>
                </div>

                <div>
                  <Label htmlFor="client_id">Client *</Label>
                  <Select value={uploadForm.client_id} onValueChange={(value) => setUploadForm({...uploadForm, client_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} ({client.organization_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={uploadForm.category} onValueChange={(value) => setUploadForm({...uploadForm, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="financial_year">Financial Year</Label>
                  <Select value={uploadForm.financial_year} onValueChange={(value) => setUploadForm({...uploadForm, financial_year: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select financial year (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {financialYears.map((fy) => (
                        <SelectItem key={fy.value} value={fy.value}>
                          {fy.label} {fy.is_current && '(Current)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    placeholder="Enter tags separated by commas"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    placeholder="Brief description of the document"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={uploading}
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Add Category Modal */}
        {showCategoryModal && (
          <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                  <Plus className="w-5 h-5 mr-2 text-blue-600" />
                  Create New Category
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Add a custom document category for better organization
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <Label htmlFor="category_name">Category Name *</Label>
                  <Input
                    id="category_name"
                    placeholder="e.g., Legal Documents, Contracts, Certificates"
                    value={categoryForm.category_name}
                    onChange={(e) => setCategoryForm({...categoryForm, category_name: e.target.value})}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This will be used for organizing and filtering documents
                  </p>
                </div>

                <div>
                  <Label htmlFor="category_label">Display Label (Optional)</Label>
                  <Input
                    id="category_label"
                    placeholder="Custom display name (leave empty to use category name)"
                    value={categoryForm.category_label}
                    onChange={(e) => setCategoryForm({...categoryForm, category_label: e.target.value})}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Category Guidelines:</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>• Use descriptive names for easy identification</p>
                    <p>• Categories help organize documents by type</p>
                    <p>• You can edit or delete custom categories later</p>
                    <p>• Default categories (Invoices, Receipts, etc.) cannot be modified</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowCategoryModal(false);
                    setCategoryForm({ category_name: '', category_label: '' });
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Category
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default Documents;
